import { styled, Theme } from 'baseui'
import { FlexCenterY } from '@/shared/theme/styled-components'
import { pulseLighting } from '@/shared/theme/pulseLighting'
import type { PillState } from './fidelityJourney'

export const StyledContainer = styled<'div', { $mastered?: boolean }>('div', ({ $theme, $mastered }) => ({
  width: '100%',
  border: `0.1rem solid ${$mastered ? $theme.colors.warning400 : 'transparent'}`,
  borderRadius: $theme.borders.radius300,
}))

export const StyledTopContainer = styled('div', () => ({
  width: '100%',
  padding: '0 1rem 1rem 1rem',
}))

export const StyledCurrentValue = styled<'span', { $reachedMax?: boolean }>('span', ({ $theme, $reachedMax }) => ({
  color: $reachedMax ? $theme.colors.positive600 : $theme.colors.mono500,
}))

export const StyledSubtitle = styled('p', ({ $theme }) => ({
  margin: '0 0 0.75rem 0',
  fontSize: '0.85rem',
  color: $theme.colors.textSecondaryAlt,
}))

export const StyledProgressBarContainer = styled('div', () => ({
  width: '100%',
}))

export const StyledProgressTrack = styled<'div', { $mastered?: boolean }>('div', ({ $theme, $mastered }) => ({
  position: 'relative',
  width: '100%',
  height: $theme.sizing.scale400,
  borderRadius: '0.5rem',
  overflow: 'hidden',
  background: $mastered ? $theme.colors.positive500 : 'linear-gradient(90deg, #FFA051 0%, #FFD152 55%, #25A337 100%)',
}))

export const StyledProgressMask = styled<'div', { $percentDone: number }>('div', ({ $percentDone }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  width: `${100 - $percentDone}%`,
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  transition: 'width 0.3s ease',
}))

export const StyledItemRow = styled<'div', { $done?: boolean }>('div', ({ $theme, $done }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0.5rem 1rem',
  backgroundColor: $done ? $theme.colors.positive50 : 'transparent',
}))

export const StyledItemLeft = styled(FlexCenterY, {
  gap: '0.5rem',
})

export const StyledItemLabel = styled<'span', { $done?: boolean }>('span', ({ $theme, $done }) => ({
  color: $done ? $theme.colors.positive600 : $theme.colors.textHeading,
}))

export const StyledDoneIcon = styled('span', ({ $theme }) => ({
  display: 'inline-flex',
  color: $theme.colors.positive500,
}))

export const StyledPendingIcon = styled('span', ({ $theme }) => ({
  display: 'inline-flex',
  color: $theme.colors.mono500,
}))

interface PulseProp {
  $pulse?: boolean
}

const radius = (value: string) => ({
  borderTopLeftRadius: value,
  borderTopRightRadius: value,
  borderBottomLeftRadius: value,
  borderBottomRightRadius: value,
})

const border = (color: string) => ({
  borderTopWidth: '1px',
  borderBottomWidth: '1px',
  borderLeftWidth: '1px',
  borderRightWidth: '1px',
  borderTopStyle: 'solid' as const,
  borderBottomStyle: 'solid' as const,
  borderLeftStyle: 'solid' as const,
  borderRightStyle: 'solid' as const,
  borderTopColor: color,
  borderBottomColor: color,
  borderLeftColor: color,
  borderRightColor: color,
})

const pulseBadgeColors = ($theme: Theme, $muted?: boolean) =>
  $muted
    ? { color: $theme.colors.textPulseMuted, backgroundColor: $theme.colors.bgPulseTrack }
    : { color: $theme.colors.primaryPulse, backgroundColor: $theme.colors.bgPulseSelected }

export const StyledBadge = styled<'span', { $muted?: boolean } & PulseProp>('span', ({ $theme, $muted, $pulse }) =>
  $pulse
    ? {
        flex: '0 0 auto',
        paddingTop: '2px',
        paddingBottom: '2px',
        paddingLeft: '9px',
        paddingRight: '9px',
        ...radius('20px'),
        fontSize: '11.5px',
        fontWeight: 600,
        letterSpacing: '0.01em',
        whiteSpace: 'nowrap',
        ...pulseBadgeColors($theme, $muted),
      }
    : {
        flex: '0 0 auto',
        padding: '0.15rem 0.5rem',
        borderRadius: '999px',
        fontSize: '0.7rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: $muted ? $theme.colors.textMuted : $theme.colors.white,
        backgroundColor: $muted ? $theme.colors.borderLight : $theme.colors.warning400,
      },
)

export const StyledSteps = styled<'div', PulseProp>('div', ({ $pulse }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: $pulse ? '8px' : '0.35rem',
  margin: $pulse ? '0 0 12px' : '0 0 0.75rem',
}))

export const StyledStep = styled<'div', PulseProp>('div', ({ $pulse }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: $pulse ? '6px' : '0.3rem',
}))

interface StepStateProps extends PulseProp {
  $done?: boolean
  $current?: boolean
}

const pulseMarkerColors = ($theme: Theme, $done?: boolean, $current?: boolean) => {
  if ($done) return { color: $theme.colors.chipOkText, backgroundColor: $theme.colors.noticeOkBg }
  if ($current) return { color: $theme.colors.white, backgroundColor: $theme.colors.primaryPulse }
  return { color: $theme.colors.textPulseMuted, backgroundColor: $theme.colors.bgPulseTrack }
}

export const StyledStepMarker = styled<'span', StepStateProps>('span', ({ $theme, $done, $current, $pulse }) =>
  $pulse
    ? {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '20px',
        height: '20px',
        ...radius('9999px'),
        fontSize: '11px',
        fontWeight: 700,
        ...pulseMarkerColors($theme, $done, $current),
      }
    : {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '1.15rem',
        height: '1.15rem',
        borderRadius: '50%',
        fontSize: '0.7rem',
        fontWeight: 700,
        color: $done || $current ? $theme.colors.white : $theme.colors.textMuted,
        backgroundColor: $done
          ? $theme.colors.positive500
          : $current
            ? $theme.colors.primary
            : $theme.colors.borderLight,
      },
)

const pulseStepLabelColor = ($theme: Theme, $done?: boolean, $current?: boolean) => {
  if ($done) return $theme.colors.chipOkText
  if ($current) return $theme.colors.textPulseStrong
  return $theme.colors.textPulseMuted
}

export const StyledStepLabel = styled<'span', StepStateProps>('span', ({ $theme, $done, $current, $pulse }) =>
  $pulse
    ? {
        fontSize: '12.5px',
        whiteSpace: 'nowrap',
        fontWeight: $current ? 600 : 400,
        color: pulseStepLabelColor($theme, $done, $current),
      }
    : {
        fontSize: '0.75rem',
        fontWeight: $current ? 700 : 400,
        color: $done || $current ? $theme.colors.textHeading : $theme.colors.textMuted,
      },
)

export const StyledStepConnector = styled<'span', { $done?: boolean } & PulseProp>(
  'span',
  ({ $theme, $done, $pulse }) =>
    $pulse
      ? {
          flex: '1 1 auto',
          minWidth: '16px',
          height: '2px',
          ...radius('2px'),
          backgroundColor: $done ? $theme.colors.successPulse : $theme.colors.borderPulse,
        }
      : {
          flex: '1 1 auto',
          height: '1px',
          backgroundColor: $done ? $theme.colors.positive500 : $theme.colors.borderLight,
        },
)

export const StyledCodeBox = styled<'div', PulseProp>('div', ({ $theme, $pulse }) =>
  $pulse
    ? {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '6px',
        paddingTop: '12px',
        paddingBottom: '12px',
        paddingLeft: '12px',
        paddingRight: '12px',
        marginBottom: '12px',
        ...radius('10px'),
        backgroundColor: $theme.colors.bgPulseCanvas,
        ...border($theme.colors.borderPulse),
      }
    : {
        padding: '0.75rem',
        textAlign: 'center',
        borderRadius: '8px',
        backgroundColor: $theme.colors.bgSubtle,
        border: `1px solid ${$theme.colors.borderLight}`,
      },
)

interface CodeStateProps extends PulseProp {
  $expired?: boolean
}

export const StyledCode = styled<'div', CodeStateProps>('div', ({ $theme, $pulse, $expired }) =>
  $pulse
    ? {
        fontFamily: 'ui-monospace, Consolas, monospace',
        fontSize: '28px',
        fontWeight: 700,
        letterSpacing: '0.24em',
        color: $expired ? $theme.colors.textPulseFaint : $theme.colors.textPulseStrong,
        textDecoration: $expired ? 'line-through' : 'none',
      }
    : {
        fontSize: '2rem',
        fontWeight: 700,
        letterSpacing: '0.2em',
        color: $theme.colors.primary,
      },
)

export const StyledCodeExpiry = styled<'p', CodeStateProps>('p', ({ $theme, $pulse, $expired }) =>
  $pulse
    ? {
        margin: 0,
        fontSize: '12px',
        fontWeight: $expired ? 600 : 400,
        color: $expired ? $theme.colors.dangerPulseText : $theme.colors.textPulseMuted,
      }
    : {
        margin: '0.25rem 0 0.5rem',
        fontSize: '0.8rem',
        color: $theme.colors.textMuted,
      },
)

const PILL_RADIUS = {
  borderTopLeftRadius: '999px',
  borderTopRightRadius: '999px',
  borderBottomLeftRadius: '999px',
  borderBottomRightRadius: '999px',
}

// baseui rounds the bar with the four longhand radii and slides the fill with a transform, so a
// `borderRadius` shorthand loses the merge and a transition on `width` animates nothing.
export const CodeProgressOverrides = {
  BarContainer: {
    style: () => ({ width: '100%', marginTop: 0, marginRight: 0, marginBottom: 0, marginLeft: 0 }),
  },
  Bar: {
    style: ({ $theme }: { $theme: Theme }) => ({
      ...PILL_RADIUS,
      width: '100%',
      height: '0.3rem',
      backgroundColor: $theme.colors.borderLight,
    }),
  },
  BarProgress: {
    style: ({ $theme }: { $theme: Theme }) => ({
      ...PILL_RADIUS,
      backgroundColor: $theme.colors.primary,
      transition: 'transform 1s linear',
    }),
  },
}

export const PulseCodeProgressOverrides = {
  ...CodeProgressOverrides,
  Bar: {
    style: ({ $theme }: { $theme: Theme }) => ({
      ...radius('4px'),
      width: '100%',
      height: '4px',
      backgroundColor: $theme.colors.borderPulse,
    }),
  },
  BarProgress: {
    style: ({ $theme }: { $theme: Theme }) => ({
      ...radius('4px'),
      backgroundColor: $theme.colors.primaryPulse,
      transition: 'transform 1s linear',
    }),
  },
}

export const StyledInfo = styled<'p', PulseProp>('p', ({ $theme, $pulse }) =>
  $pulse
    ? { margin: '10px 0 0', fontSize: '12px', lineHeight: '17px', color: $theme.colors.textPulseMuted }
    : {
        margin: '0.5rem 0 0',
        fontSize: '0.85rem',
        lineHeight: 1.45,
        color: $theme.colors.textSecondaryAlt,
      },
)

export const StyledScript = styled<'p', PulseProp>('p', ({ $theme, $pulse }) =>
  $pulse
    ? { margin: '0 0 12px', fontSize: '13px', lineHeight: '19px', color: $theme.colors.textPulseLabel }
    : {
        margin: '0 0 0.5rem',
      },
)

export const StyledSentBox = styled<'div', PulseProp>('div', ({ $theme, $pulse }) =>
  $pulse
    ? {
        margin: '0 0 12px',
        paddingTop: '8px',
        paddingBottom: '8px',
        paddingLeft: '10px',
        paddingRight: '10px',
        ...radius('8px'),
        backgroundColor: $theme.colors.bgPulseCanvas,
        fontSize: '12.5px',
        lineHeight: '17px',
        color: $theme.colors.textPulseLabel,
      }
    : {
        margin: '0 0 0.5rem',
        padding: '0.5rem',
        borderRadius: $theme.borders.radius200,
        backgroundColor: $theme.colors.successBg,
      },
)

export const StyledSentTitle = styled<'strong', PulseProp>('strong', ({ $theme, $pulse }) =>
  $pulse ? { fontWeight: 600, color: $theme.colors.textPulseStrong } : { fontWeight: 700 },
)

export const StyledSentBadge = styled('div', {
  marginTop: '6px',
})

const pillColors = ($theme: Theme, $state: PillState) => {
  if ($state === 'ok') {
    return { color: $theme.colors.chipOkText, backgroundColor: $theme.colors.chipOkBg, borderColor: 'transparent' }
  }
  if ($state === 'wait') {
    return {
      color: $theme.colors.chipWarnText,
      backgroundColor: $theme.colors.chipWarnBg,
      borderColor: $theme.colors.chipWarnBorder,
    }
  }
  return {
    color: $theme.colors.textPulseMuted,
    backgroundColor: $theme.colors.bgPulseTrack,
    borderColor: 'transparent',
  }
}

export const StyledJourney = styled('div', {
  width: '100%',
})

export const StyledPills = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px',
  marginBottom: '12px',
})

export const StyledPill = styled<'span', { $state: PillState }>('span', ({ $theme, $state }) => {
  const { color, backgroundColor, borderColor } = pillColors($theme, $state)

  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    boxSizing: 'border-box',
    height: '24px',
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: '7px',
    paddingRight: '9px',
    ...radius('9999px'),
    ...border(borderColor),
    fontSize: '12px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    color,
    backgroundColor,
  }
})

export const StyledKicker = styled('p', ({ $theme }) => ({
  margin: '0 0 8px',
  fontSize: '12px',
  lineHeight: '16px',
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: $theme.colors.textPulseMuted,
}))

export const StyledChoices = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '8px',
  marginBottom: '12px',
})

export const StyledChoice = styled<'button', { $selected: boolean }>('button', ({ $theme, $selected }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '4px',
  paddingTop: '10px',
  paddingBottom: '10px',
  paddingLeft: '12px',
  paddingRight: '12px',
  ...radius('10px'),
  ...border($selected ? $theme.colors.primaryPulse : $theme.colors.borderPulse),
  backgroundColor: $selected ? $theme.colors.bgPulseSelected : $theme.colors.white,
  boxShadow: $selected ? pulseLighting($theme).shadowPulseFocus : 'none',
  fontFamily: 'inherit',
  textAlign: 'left',
  color: $theme.colors.textPulseStrong,
  cursor: 'pointer',
  ':hover': $selected
    ? {}
    : { ...border($theme.colors.borderPulseHover), backgroundColor: $theme.colors.bgPulseCanvas },
  ':focus-visible': { outline: `2px solid ${$theme.colors.primaryPulse}`, outlineOffset: '2px' },
}))

export const StyledChoiceTitle = styled('span', ({ $theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '13.5px',
  lineHeight: '18px',
  fontWeight: 600,
  color: $theme.colors.textPulseStrong,
}))

export const StyledChoiceIcon = styled('span', ({ $theme }) => ({
  display: 'inline-flex',
  color: $theme.colors.primaryPulse,
}))

export const StyledChoiceDescription = styled('span', ({ $theme }) => ({
  fontSize: '12px',
  lineHeight: '16px',
  color: $theme.colors.textPulseMuted,
}))

export const StyledHelp = styled('details', {
  marginBottom: '12px',
})

export const StyledHelpSummary = styled('summary', ({ $theme }) => ({
  cursor: 'pointer',
  paddingTop: '10px',
  paddingBottom: '10px',
  paddingLeft: '12px',
  paddingRight: '12px',
  ...radius('8px'),
  ...border($theme.colors.borderPulse),
  backgroundColor: $theme.colors.bgPulseCanvas,
  color: $theme.colors.primaryPulse,
  fontSize: '13px',
  lineHeight: '18px',
  fontWeight: 600,
}))

export const StyledHelpBody = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  marginTop: '10px',
})

export const StyledHelpNote = styled('p', ({ $theme }) => ({
  margin: 0,
  paddingTop: '8px',
  paddingBottom: '8px',
  paddingLeft: '10px',
  paddingRight: '10px',
  ...radius('8px'),
  backgroundColor: $theme.colors.bgPulseCanvas,
  fontSize: '12.5px',
  lineHeight: '17px',
  color: $theme.colors.textPulseLabel,
}))

export const StyledScreenshot = styled('img', ({ $theme }) => ({
  display: 'block',
  width: '100%',
  height: 'auto',
  ...radius('10px'),
  ...border($theme.colors.borderPulse),
}))

export const StyledScreenshotGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '12px',
  alignItems: 'start',
})

export const StyledFigure = styled('figure', {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  margin: 0,
})

export const StyledCaption = styled('figcaption', ({ $theme }) => ({
  fontSize: '12px',
  lineHeight: '17px',
  color: $theme.colors.textPulseMuted,
}))

export const StyledVerifyColumn = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
})

export const StyledVerifyText = styled('p', ({ $theme }) => ({
  margin: 0,
  fontSize: '13px',
  lineHeight: '19px',
  color: $theme.colors.textPulseLabel,
}))

export const StyledWaitBox = styled('div', ({ $theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '12px',
  paddingTop: '8px',
  paddingBottom: '8px',
  paddingLeft: '10px',
  paddingRight: '10px',
  ...radius('8px'),
  ...border($theme.colors.borderPulseHover),
  borderTopStyle: 'dashed',
  borderBottomStyle: 'dashed',
  borderLeftStyle: 'dashed',
  borderRightStyle: 'dashed',
  backgroundColor: $theme.colors.bgPulseCanvas,
  fontSize: '12.5px',
  lineHeight: '17px',
  color: $theme.colors.textPulseLabel,
}))

export const StyledSpinner = styled('span', ({ $theme }) => ({
  flexShrink: 0,
  boxSizing: 'border-box',
  width: '16px',
  height: '16px',
  ...radius('9999px'),
  ...border($theme.colors.borderPulse),
  borderTopWidth: '2px',
  borderBottomWidth: '2px',
  borderLeftWidth: '2px',
  borderRightWidth: '2px',
  borderTopColor: $theme.colors.primaryPulse,
  animationName: {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
  },
  animationDuration: '0.9s',
  animationTimingFunction: 'linear',
  animationIterationCount: 'infinite',
  '@media (prefers-reduced-motion: reduce)': {
    animationName: 'none',
  },
}))

export const StyledSeparator = styled('div', ({ $theme }) => ({
  height: '1px',
  marginBottom: '12px',
  backgroundColor: $theme.colors.bgPulseTrack,
}))

export const StyledHint = styled('p', ({ $theme }) => ({
  margin: '0 0 12px',
  fontSize: '12px',
  lineHeight: '17px',
  color: $theme.colors.textPulseMuted,
}))

export const StyledButtonBlock = styled('div', {
  marginBottom: '12px',
})

export const SoftButtonOverrides = {
  BaseButton: {
    style: ({ $theme }: { $theme: Theme }) => ({
      ...border($theme.colors.borderPulse),
      backgroundColor: $theme.colors.bgPulseCanvas,
      color: $theme.colors.primaryPulse,
      ':hover': { backgroundColor: $theme.colors.bgPulseSelected },
    }),
  },
}
